"use client"

import { formatCurrency } from "./calculator-ui"
import { SolveForWidget, type SolveForField } from "./solve-for-widget"

/**
 * CPM = cost ÷ impressions × 1,000 — solve for any one of the three.
 *
 * Default example: $2,500 of spend delivering 250,000 impressions = $10 CPM.
 * That is an arithmetic example, not a benchmark; the page deliberately ships
 * no platform CPM figures because none of them are published by the platforms.
 */

type CpmKey = "cpm" | "cost" | "impressions"

const formatCount = (n: number) =>
  Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—"

/** One impression's cost is a fraction of a cent, so use significant digits. */
const formatPerImpression = (cpm: number) =>
  `$${(cpm / 1000).toLocaleString("en-US", { maximumSignificantDigits: 2 })}`

const fields = [
  {
    key: "cpm",
    tabLabel: "CPM",
    label: "CPM (cost per 1,000 impressions)",
    resultLabel: "CPM",
    defaultValue: "10",
    prefix: "$",
    format: (n) => formatCurrency(n),
  },
  {
    key: "cost",
    tabLabel: "Cost",
    label: "Ad spend",
    resultLabel: "Ad spend required",
    defaultValue: "2500",
    prefix: "$",
    format: (n) => formatCurrency(n),
  },
  {
    key: "impressions",
    tabLabel: "Impressions",
    label: "Impressions",
    resultLabel: "Impressions bought",
    defaultValue: "250000",
    step: 1,
    format: formatCount,
  },
] as const satisfies readonly [
  SolveForField<CpmKey>,
  SolveForField<CpmKey>,
  SolveForField<CpmKey>,
]

export function CpmWidget() {
  return (
    <SolveForWidget<CpmKey>
      title="CPM Calculator"
      idPrefix="cpm"
      fields={fields}
      solve={(target, { cpm, cost, impressions }) => {
        if (target === "cpm") {
          if (cost === null || impressions === null) return null
          if (cost < 0 || impressions <= 0) return null
          const value = (cost / impressions) * 1000
          return {
            value,
            caption: `You paid ${formatCurrency(value)} for every 1,000 impressions — about ${formatPerImpression(value)} per single impression.`,
          }
        }
        if (target === "cost") {
          if (cpm === null || impressions === null) return null
          if (cpm < 0 || impressions < 0) return null
          const value = (cpm * impressions) / 1000
          return {
            value,
            caption: `${formatCount(impressions)} impressions at a ${formatCurrency(cpm)} CPM costs ${formatCurrency(value)} in media. Creative production, tooling and agency fees sit on top of this.`,
          }
        }
        if (cost === null || cpm === null) return null
        if (cost < 0 || cpm <= 0) return null
        const value = (cost / cpm) * 1000
        return {
          value,
          caption: `${formatCurrency(cost)} buys about ${formatCount(value)} impressions at a ${formatCurrency(cpm)} CPM. Impressions are not people — divide by your expected frequency to estimate reach.`,
        }
      }}
      invalidCaption={(target) =>
        target === "cpm"
          ? "Enter ad spend and a non-zero impression count."
          : target === "cost"
            ? "Enter a CPM and an impression count."
            : "Enter a budget and a non-zero CPM."
      }
      formulaNote={(target) =>
        target === "cpm"
          ? "CPM = Ad spend ÷ Impressions × 1,000. Take both numbers from the same campaign and the same date range."
          : target === "cost"
            ? "Ad spend = CPM × Impressions ÷ 1,000."
            : "Impressions = Ad spend ÷ CPM × 1,000."
      }
    />
  )
}
